import React, { useState } from "react";
import {
  Box,
  styled,
  Divider,
  Typography,
  Button as MuiButton,
  Stack,
  IconButton,
  Grid,
  Checkbox
} from "@mui/material";
import { SearchField, DateRangePicker, MultipleInput } from "../inputs";

export interface SidebarNavigationInterface {
  open?: boolean;
  types?: { value: string; label: string }[];
  categories?: { value: string; label: string }[];
  locations?: { value: string; label: string }[];
  eventProviders?: { value: string; label: string }[];
  levels?: { value: string; label: string }[];
  onClose: () => void;
  filterOptions: { value: string; label: string }[];
}

const StyledIconButton = styled(IconButton)(({ theme }: any) => ({
  width: "100%",
  borderRadius: 21,
  height: 65,
  border: `1px solid ${theme.palette.common.borderGrey}`,
  "& .far": {
    fontSize: 35
  }
}));

const StyledButton = styled(MuiButton)(() => ({
  padding: "8px 14px",
  fontSize: 14,
  height: 38,
  borderRadius: 8,
  "& .far": {
    fontSize: "14px !important"
  }
}));

const StyledCheckbox = styled(Checkbox)(() => ({
  padding: 0,
  "& .fas": {
    fontSize: 20
  }
}));

const items = [
  {
    value: "inPerson",
    label: "In-Person"
  },
  {
    value: "online",
    label: "Online"
  }
];

const SidebarNavigation = ({
  open,
  filterOptions,
  onClose,
  types,
  categories,
  locations,
  eventProviders,
  levels
}: SidebarNavigationInterface) => {
  const [selectedFilters, setSelectedFilters] = useState<
    { label: string; value: string }[]
  >([]);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [eventType, setEventType] = React.useState<string[]>([]);
  const [category, setCategories] = React.useState<string[]>([]);
  const [location, setLocation] = React.useState<string[]>([]);
  const [eventProvider, setEventProvider] = React.useState<string[]>([]);
  const [level, setLevel] = React.useState<string[]>([]);

  const [filters, setFilters] = useState(false);

  return (
    <Stack>
      {open
        ? <Stack py={11} pr={3} pl={7.5}>
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h3" fontWeight={700}>
                Filters
              </Typography>
              <StyledButton
                className="font-primary"
                variant="contained"
                startIcon={<i className="far fa-arrow-left" />}
                onClick={() => onClose()}
                color="info"
              >
                Hide
              </StyledButton>
            </Stack>

            <Stack pt={5} spacing={2}>
              <Stack spacing={2}>
                <SearchField
                  placeholder="Keyword"
                  searchIconPosition="end"
                  height={40}
                  fontSize={14}
                />
                <Box textAlign="end">
                  <StyledButton
                    className="font-primary"
                    startIcon={
                      <img
                        src="http://e360dev.zeforgesolutions.com/wp-content/themes/e360/assets/images/page_events/delete-bin.svg"
                        alt="trash"
                      />
                    }
                    onClick={() => setSelectedFilters([])}
                  >
                    Clear Filters
                  </StyledButton>
                </Box>
              </Stack>
              <Divider />
              <Stack py={1.5}>
                <Grid container spacing={1}>
                  {filterOptions.map(filter =>
                    <Grid item xs={6}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <StyledCheckbox
                          checked={
                            selectedFilters.findIndex(
                              i => i.value === filter.value
                            ) > -1
                          }
                          color="primary"
                          onClick={() => {
                            const temp = [...selectedFilters];
                            const index = selectedFilters.findIndex(
                              selected => selected.value === filter.value
                            );
                            if (index > -1) {
                              temp.splice(index, 1);
                              setSelectedFilters(temp);
                            } else
                              setSelectedFilters([...selectedFilters, filter]);
                          }}
                          checkedIcon={
                            <i className="fas fa-check-square font-primary" />
                          }
                          icon={
                            <i className="fas fa-square font-backgroundGrey" />
                          }
                        />
                        <Typography variant="h5" fontWeight={400}>
                          {filter.label}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Stack>
              <Divider />
              <Stack py={2.5} spacing={2}>
                <Typography variant="h5" fontWeight={700}>
                  Choose Date Range
                </Typography>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={newDate => setStartDate(newDate)}
                  onEndDateChange={newDate => setEndDate(newDate)}
                />
              </Stack>
              <Divider />
              <Stack py={1.5}>
                <Grid container>
                  {items.map(item =>
                    <Grid item xs={6}>
                      <Stack direction="row" alignItems="center">
                        <StyledCheckbox
                          checked={
                            selectedFilters.findIndex(
                              i => i.value === item.value
                            ) > -1
                          }
                          color="primary"
                          onClick={() => {
                            const temp = [...selectedFilters];
                            const index = selectedFilters.findIndex(
                              selected => selected.value === item.value
                            );
                            if (index > -1) {
                              temp.splice(index, 1);
                              setSelectedFilters(temp);
                            } else
                              setSelectedFilters([...selectedFilters, item]);
                          }}
                          checkedIcon={
                            <i className="fas fa-check-square font-primary" />
                          }
                          icon={
                            <i className="fas fa-square font-backgroundGrey" />
                          }
                        />
                        <Typography variant="h5" fontWeight={400}>
                          {item.label}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Stack>
              <Divider />
              <Stack py={2.5} spacing={2}>
                {types &&
                  <MultipleInput
                    placeholder="Event Type"
                    value={eventType}
                    handleChange={(value: any) =>
                      setEventType(
                        typeof value === "string" ? value.split(",") : value
                      )}
                    items={types}
                  />}
                {locations &&
                  <MultipleInput
                    placeholder="Location"
                    items={locations}
                    value={location}
                    handleChange={(value: any) =>
                      setLocation(
                        typeof value === "string" ? value.split(",") : value
                      )}
                  />}

                {categories &&
                  <MultipleInput
                    placeholder="Categories"
                    items={categories}
                    value={category}
                    handleChange={(value: any) =>
                      setCategories(
                        typeof value === "string" ? value.split(",") : value
                      )}
                  />}
                {eventProviders &&
                  <MultipleInput
                    placeholder="Event Provider"
                    items={eventProviders}
                    value={eventProvider}
                    handleChange={(value: any) =>
                      setEventProvider(
                        typeof value === "string" ? value.split(",") : value
                      )}
                  />}

                {levels &&
                  <MultipleInput
                    placeholder="Job Level"
                    items={levels}
                    value={level}
                    handleChange={(value: any) =>
                      setLevel(
                        typeof value === "string" ? value.split(",") : value
                      )}
                  />}
                <MuiButton
                  variant="contained"
                  onClick={() => setFilters(!filters)}
                >
                  Apply Filters
                </MuiButton>
              </Stack>
            </Stack>
          </Stack>
        : <Stack p={4}>
            <StyledIconButton className="background-primary" onClick={onClose}>
              <i className="far fa-filter font-white" />
            </StyledIconButton>
          </Stack>}
    </Stack>
  );
};

export default SidebarNavigation;
